import React, { useState, useEffect, useRef } from 'react';
import { Modal, Dropdown } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axiosClient from '../../services/axiosClient';

function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const userData = JSON.parse(str) || {};
		
		// Si on est en train d'emprunter une identité, utiliser le token d'emprunt
		if (userData.isImpersonating && userData.impersonation_token) {
			return { Authorization: `Bearer ${userData.impersonation_token}` };
		}
		
		// Sinon utiliser le token normal
		const { token, tokenType } = userData;
		if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		return {};
	} catch (_) {
		return {};
	}
}

const UsersList = () => {
	const navigate = useNavigate();
	const [users, setUsers] = useState([]);
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [recordsPerPage, setRecordsPerPage] = useState(25);
	const [view, setView] = useState('list'); // 'list' or 'edit'
	const [currentUser, setCurrentUser] = useState(null);
	const [roles, setRoles] = useState([]);
	const [permissions, setPermissions] = useState([]);
	
	// Pagination states (server-side)
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalUsers, setTotalUsers] = useState(0);
	
	// Modal states
	const [showModal, setShowModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showImpersonateModal, setShowImpersonateModal] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);
	const [userToImpersonate, setUserToImpersonate] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [impersonating, setImpersonating] = useState(false);
	const [formData, setFormData] = useState({
		name: '',
		username: '',
		email: '',
		password: '',
		password_confirmation: '',
		phone: '',
		gender_id: 1,
		user_type_id: 1,
		roles: [],
		permissions: [],
		email_verified: false,
		phone_verified: false
	});
	const [formErrors, setFormErrors] = useState({});

	useEffect(() => {
		fetchUsers();
		fetchRoles();
		fetchPermissions();
	}, []);

	useEffect(() => {
		fetchUsers();
	}, [recordsPerPage, currentPage, searchTerm]);

	const fetchUsers = async () => {
		try {
			setLoading(true);
			// Try different parameter names for pagination
			let url = `/users?per_page=${recordsPerPage}&perPage=${recordsPerPage}&limit=${recordsPerPage}&page=${currentPage}`;
			if (searchTerm) {
				url += `&search=${encodeURIComponent(searchTerm)}`;
			}
			console.log('Calling API with URL:', url);
			console.log('recordsPerPage:', recordsPerPage);
			const response = await axiosClient.get(url, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			let data = response?.data;
			let list = data?.result?.data || data?.data || data?.result || data || [];
			if (!Array.isArray(list)) {
				if (list && Array.isArray(list.data)) list = list.data;
				else list = [];
			}
			
			// Extract pagination metadata
			const meta = data?.result?.meta;
			console.log('API Response meta:', meta);
			console.log('API returned', list.length, 'users');
			if (meta) {
				setTotalPages(meta.last_page);
				setTotalUsers(meta.total);
			}
			
			const normalized = list.map(u => ({
				id: u.id,
				name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
				email: u.email,
				country: u.country_code || u.country,
				email_verified: u.email_verified_at ? 1 : 0,
				created_at: u.created_at_formatted || u.created_at,
				photo_url: u.photo_url,
			}));
			
			setUsers(normalized);
			setFilteredUsers(normalized);
		} catch (err) {
			console.error('Error fetching users:', err);
		} finally {
			setLoading(false);
		}
	};

	const fetchRoles = async () => {
		try {
			const response = await axiosClient.get('/admin/roles', {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			const list = response?.data?.result || response?.data?.data || [];
			setRoles(Array.isArray(list) ? list : []);
		} catch (err) {
			console.error('Error fetching roles:', err);
		}
	};

	const fetchPermissions = async () => {
		try {
			const response = await axiosClient.get('/admin/permissions', {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			const list = response?.data?.result || response?.data?.data || [];
			setPermissions(Array.isArray(list) ? list : []);
		} catch (err) {
			console.error('Error fetching permissions:', err);
		}
	};

	const handleEditUser = async (user) => {
		try {
			// Charger les détails complets de l'utilisateur
			const response = await axiosClient.get(`/users/${user.id}`, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			const userData = response?.data?.result || response?.data;
			
			setCurrentUser(userData);
			setFormData({
				name: userData.name || '',
				username: userData.username || userData.name || '',
				email: userData.email || '',
				password: '',
				password_confirmation: '',
				phone: userData.phone || '',
				gender_id: userData.gender_id || 1,
				user_type_id: userData.user_type_id || 1,
				roles: userData.roles?.map(r => r.name || r) || [],
				permissions: userData.permissions?.map(p => p.name || p) || [],
				email_verified: !!userData.email_verified_at,
				phone_verified: !!userData.phone_verified_at
			});
			setView('edit');
		} catch (err) {
			console.error('Error loading user details:', err);
			alert('Erreur lors du chargement des détails de l\'utilisateur');
		}
	};

	const handleUpdateUser = async () => {
		try {
			setSubmitting(true);
			const payload = {
				user_type_id: formData.user_type_id,
				gender_id: formData.gender_id,
				name: formData.name,
				email: formData.email,
				username: formData.username || formData.name,
				phone_hidden: false,
				phone: formData.phone,
				phone_country: 'SN',
				country_code: 'SN',
				language_code: 'fr',
				auth_field: 'email',
				accept_terms: true,
				accept_marketing_offers: false,
				time_zone: 'Africa/Dakar',
				roles: formData.roles,
				permissions: formData.permissions
			};
			
			// Ajouter le mot de passe seulement s'il est fourni
			if (formData.password && formData.password.trim()) {
				payload.password = formData.password;
				payload.password_confirmation = formData.password_confirmation;
			}
			
			await axiosClient.put(`/users/${currentUser.id}`, payload, {
				headers: { 
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					'X-AppApiToken': 'senoffre_api_token_2024_secure_key_xyz123',
					...getAuthHeader() 
				},
			});
			
			alert('Utilisateur mis à jour avec succès');
			setView('list');
			await fetchUsers();
		} catch (err) {
			console.error('Error updating user:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de la mise à jour de l\'utilisateur';
			alert(errorMsg);
		} finally {
			setSubmitting(false);
		}
	};

	useEffect(() => {
		// Reset pagination to page 1 when search term changes
		setCurrentPage(1);
	}, [searchTerm]);

	// Set filteredUsers to users since we're using server-side pagination
	useEffect(() => {
		setFilteredUsers(users);
	}, [users]);

	// Server-side pagination logic - show max 5 pages like in the image
	const getVisiblePages = () => {
		const maxVisible = 5;
		let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
		let endPage = Math.min(totalPages, startPage + maxVisible - 1);
		
		// Adjust start if we're near the end
		if (endPage - startPage + 1 < maxVisible) {
			startPage = Math.max(1, endPage - maxVisible + 1);
		}
		
		const pages = [];
		for (let i = startPage; i <= endPage; i++) {
			pages.push(i);
		}
		return pages;
	};

	const handlePageClick = (pageNumber) => {
		setCurrentPage(pageNumber);
	};

	const handlePrevious = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1);
		}
	};

	const handleNext = () => {
		if (currentPage < totalPages) {
			setCurrentPage(currentPage + 1);
		}
	};

	useEffect(() => {
		// Reset to first page when recordsPerPage changes
		setCurrentPage(1);
	}, [recordsPerPage]);

	const handleDelete = (user) => {
		setUserToDelete(user);
		setShowDeleteModal(true);
	};

	const confirmDelete = async () => {
		if (!userToDelete) return;
		
		try {
			setDeleting(true);
			await axiosClient.delete(`/users/${userToDelete.id}`, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			await fetchUsers();
			setShowDeleteModal(false);
			setUserToDelete(null);
		} catch (err) {
			console.error('Error deleting user:', err);
			const errorMessage = err.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur';
			alert(errorMessage);
		} finally {
			setDeleting(false);
		}
	};

	const cancelDelete = () => {
		setShowDeleteModal(false);
		setUserToDelete(null);
	};

	const handleImpersonateUser = (user) => {
		setUserToImpersonate(user);
		setShowImpersonateModal(true);
	};

	const confirmImpersonate = async () => {
		if (!userToImpersonate) return;
		
		try {
			setImpersonating(true);
			// Appel API pour emprunter l'identité avec le bon endpoint
			const response = await axiosClient.post(`/admin/impersonate/take/${userToImpersonate.id}`, {}, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			if (response.data.success) {
				// Sauvegarder les informations d'emprunt
				const impersonationData = {
					...response.data.result,
					isImpersonating: true,
					impersonation_token: response.data.result.impersonation_token,
					original_admin: response.data.result.original_admin,
					impersonated_user: response.data.result.impersonated_user
				};
				
				localStorage.setItem('userDetails', JSON.stringify(impersonationData));
				
				// Rediriger vers le dashboard approprié selon le type d'utilisateur emprunté
				const impersonatedUser = response.data.result.impersonated_user;
				console.log('Impersonated user:', impersonatedUser);
				console.log('User type ID:', impersonatedUser.user_type_id);
				
				// Attendre un peu pour que le localStorage soit mis à jour
				setTimeout(() => {
					if (impersonatedUser.user_type_id === 1) {
						// Entreprise
						console.log('Redirecting to entreprise dashboard');
						window.location.href = '/entreprise/dashboard';
					} else if (impersonatedUser.user_type_id === 2) {
						// Consultant
						console.log('Redirecting to consultant dashboard');
						window.location.href = '/consultant/dashboard';
					} else {
						// Fallback pour autres types
						console.log('Redirecting to entreprise dashboard (fallback)');
						window.location.href = '/entreprise/dashboard';
					}
				}, 100);
			}
		} catch (err) {
			console.error('Error impersonating user:', err);
			const errorMessage = err.response?.data?.message || 'Erreur lors de l\'emprunt d\'identité';
			alert(errorMessage);
		} finally {
			setImpersonating(false);
			setShowImpersonateModal(false);
			setUserToImpersonate(null);
		}
	};

	const cancelImpersonate = () => {
		setShowImpersonateModal(false);
		setUserToImpersonate(null);
	};

	const handleStopImpersonation = async () => {
		if (!window.confirm('Voulez-vous arrêter l\'emprunt d\'identité ?')) return;
		
		try {
			const response = await axiosClient.post('/admin/impersonate/leave', {}, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			if (response.data.success) {
				// Restaurer les informations de l'admin original
				const originalAdminData = {
					...response.data.result.returned_to_admin,
					token: response.data.result.new_admin_token,
					tokenType: 'Bearer',
					isImpersonating: false
				};
				
				localStorage.setItem('userDetails', JSON.stringify(originalAdminData));
				
				// Rediriger vers le dashboard admin
				setTimeout(() => {
					navigate('/admin/dashboard');
				}, 100);
			}
		} catch (err) {
			console.error('Error stopping impersonation:', err);
			const errorMessage = err.response?.data?.message || 'Erreur lors de l\'arrêt de l\'emprunt';
			alert(errorMessage);
		}
	};

	// Vérifier si on est en mode emprunt
	const isCurrentlyImpersonating = () => {
		try {
			const userData = JSON.parse(localStorage.getItem('userDetails') || '{}');
			return userData.isImpersonating === true;
		} catch {
			return false;
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		// Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			return dateString;
		}
		// Sinon, formater la date
		return new Date(dateString).toLocaleDateString('fr-FR');
	};

	const handleOpenModal = () => {
		setFormData({
			name: '',
			email: '',
			password: '',
			password_confirmation: '',
			phone: '',
			gender_id: 1,
			user_type_id: 1
		});
		setFormErrors({});
		setShowModal(true);
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setFormData({
			name: '',
			email: '',
			password: '',
			password_confirmation: '',
			phone: '',
			gender_id: 1,
			user_type_id: 1
		});
		setFormErrors({});
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		// Clear error for this field
		if (formErrors[name]) {
			setFormErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const validateForm = () => {
		const errors = {};
		
		if (!formData.name.trim()) {
			errors.name = 'Le nom est requis';
		}
		
		if (!formData.email.trim()) {
			errors.email = 'L\'email est requis';
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			errors.email = 'Email invalide';
		}
		
		if (!formData.password) {
			errors.password = 'Le mot de passe est requis';
		} else if (formData.password.length < 6) {
			errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
		}
		
		if (formData.password !== formData.password_confirmation) {
			errors.password_confirmation = 'Les mots de passe ne correspondent pas';
		}
		
		if (!formData.phone.trim()) {
			errors.phone = 'Le téléphone est requis';
		}
		
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}
		
		try {
			setSubmitting(true);
			await axiosClient.post('/users', formData, {
				headers: { 
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					...getAuthHeader() 
				},
			});
			
			// Success
			handleCloseModal();
			await fetchUsers();
			alert('Utilisateur créé avec succès !');
		} catch (err) {
			console.error('Error creating user:', err);
			if (err.response?.data?.errors) {
				setFormErrors(err.response.data.errors);
			} else {
				alert('Erreur lors de la création de l\'utilisateur');
			}
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<style>
				{`
					.custom-modal-width .modal-dialog {
						max-width: 66vw !important;
						width: 66vw !important;
					}
					
					@media (max-width: 768px) {
						.custom-modal-width .modal-dialog {
							width: 95vw !important;
							max-width: 95vw !important;
						}
					}
				`}
			</style>
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex justify-content-between align-items-center">
						<div>
							<h2 style={{ fontSize: '28px', fontWeight: '700' }}>Utilisateurs</h2>
							{isCurrentlyImpersonating() && (
								<div className="alert alert-warning py-2 px-3 mb-2 d-flex justify-content-between align-items-center" style={{ fontSize: '14px' }}>
									<span>
										<i className="fas fa-user-secret me-2"></i>
										<strong>Mode emprunt d'identité actif</strong>
									</span>
									<button 
										className="btn btn-sm btn-outline-danger"
										onClick={handleStopImpersonation}
										style={{ fontSize: '12px' }}
									>
										<i className="fas fa-sign-out-alt me-1"></i>
										Arrêter l'emprunt
									</button>
								</div>
							)}
							<p className="text-muted">Affichage des éléments {((currentPage - 1) * recordsPerPage) + 1} à {Math.min(currentPage * recordsPerPage, totalUsers)} sur {totalUsers}</p>
						</div>
						<div className="d-flex gap-2">
							<button className="btn btn-outline-primary" style={{ borderRadius: '8px' }}>
								Tableau de bord
							</button>
							<button className="btn btn-outline-primary" style={{ borderRadius: '8px' }}>
								Utilisateurs
							</button>
							<button className="btn btn-primary" style={{ borderRadius: '8px' }}>
								Liste
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Action Buttons */}
			<div className="row mb-3">
				<div className="col-12">
					<div className="d-flex gap-2">
						<button 
							className="btn btn-primary" 
							style={{ borderRadius: '8px' }}
							onClick={handleOpenModal}
						>
							<i className="fas fa-plus me-2"></i>Ajouter utilisateur
						</button>
						<button 
							className="btn btn-danger" 
							disabled={selectedUsers.length === 0}
							style={{ borderRadius: '8px' }}
						>
							<i className="fas fa-times me-2"></i>Delete
						</button>
					</div>
				</div>
			</div>

			{/* Filters Row */}
			<div className="row mb-3">
				<div className="col-12">
					<div className="d-flex gap-2 flex-wrap align-items-center">
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								<i className="fas fa-calendar me-2"></i>Date range
							</Dropdown.Toggle>
						</Dropdown>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								Name
							</Dropdown.Toggle>
						</Dropdown>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								Nom d'utilisateur
							</Dropdown.Toggle>
						</Dropdown>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								Country
							</Dropdown.Toggle>
						</Dropdown>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								Has Subscription
							</Dropdown.Toggle>
						</Dropdown>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								Has Valid Subscription
							</Dropdown.Toggle>
						</Dropdown>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								Status
							</Dropdown.Toggle>
						</Dropdown>
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
								Permissions/Rules
							</Dropdown.Toggle>
						</Dropdown>
					</div>
				</div>
			</div>

			{/* Records per page and Search */}
			<div className="row mb-3">
				<div className="col-md-6">
					<Dropdown>
						<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
							{recordsPerPage}
						</Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item onClick={() => setRecordsPerPage(10)}>10</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(20)}>20</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(25)}>25</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(30)}>30</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(40)}>40</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(50)}>50</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(60)}>60</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(70)}>70</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(80)}>80</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(90)}>90</Dropdown.Item>
							<Dropdown.Item onClick={() => setRecordsPerPage(100)}>100</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
					<span className="ms-2 text-muted">enregistrement par page</span>
				</div>
				<div className="col-md-6">
					<div className="d-flex justify-content-end">
						<div className="input-group" style={{ maxWidth: '300px' }}>
							<span className="input-group-text bg-white" style={{ borderRadius: '8px 0 0 8px' }}>
								Recherche:
							</span>
							<input 
								type="text" 
								className="form-control" 
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Rechercher..."
								style={{ borderRadius: '0 8px 8px 0' }}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body p-0">
					{loading ? (
						<div className="text-center py-5">
							<div className="spinner-border text-primary"></div>
						</div>
					) : (
						<>
							<table className="table table-hover mb-0">
								<thead style={{ backgroundColor: '#f8f9fa' }}>
									<tr>
										<th><input type="checkbox" className="form-check-input" /></th>
										<th>Date</th>
										<th>Name</th>
										<th>Email</th>
										<th>Country</th>
										<th>Email vérifié</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									{filteredUsers.map((user) => (
										<tr key={user.id}>
											<td style={{ padding: '12px' }}>
												<input 
													type="checkbox" 
													className="form-check-input"
													checked={selectedUsers.includes(user.id)}
													onChange={() => {
														if (selectedUsers.includes(user.id)) {
															setSelectedUsers(selectedUsers.filter(id => id !== user.id));
														} else {
															setSelectedUsers([...selectedUsers, user.id]);
														}
													}}
												/>
											</td>
											<td style={{ padding: '12px' }}>{formatDate(user.created_at)}</td>
											<td style={{ padding: '12px' }}>
												<i className="fas fa-user-circle me-2" style={{ fontSize: '18px', color: '#6c757d' }}></i>
												{user.name}
											</td>
											<td style={{ padding: '12px' }}>{user.email}</td>
											<td style={{ padding: '12px', fontSize: '20px' }}>
												{user.country === 'SN' && '🇸🇳'}
												{user.country === 'FR' && '🇫🇷'}
												{user.country === 'US' && '🇺🇸'}
												{!['SN', 'FR', 'US'].includes(user.country) && user.country}
											</td>
											<td className="text-center" style={{ padding: '12px' }}>
												<input 
													className="form-check-input" 
													type="checkbox" 
													checked={user.email_verified === 1} 
													readOnly 
													style={{ width: '48px', height: '24px' }} 
												/>
											</td>
											<td style={{ padding: '12px' }}>
												<div className="d-flex gap-2 align-items-center">
													<Dropdown>
														<Dropdown.Toggle 
															variant="link" 
															size="sm" 
															style={{ textDecoration: 'none', color: '#6c757d', padding: '0' }}
														>
															<i className="fas fa-ellipsis-v"></i>
														</Dropdown.Toggle>
														<Dropdown.Menu>
															<Dropdown.Item>
																<i className="fas fa-edit me-2"></i>Éditer
															</Dropdown.Item>
															<Dropdown.Item className="text-danger" onClick={() => handleDelete(user)}>
																<i className="fas fa-trash me-2"></i>Delete
															</Dropdown.Item>
														</Dropdown.Menu>
													</Dropdown>
													<button 
														className={`btn btn-sm ${user.email_verified === 1 ? 'btn-warning' : 'btn-secondary'}`}
														onClick={user.email_verified === 1 ? () => handleImpersonateUser(user) : undefined}
														style={{ 
															borderRadius: '6px',
															cursor: user.email_verified === 1 ? 'pointer' : 'not-allowed'
														}}
														title={user.email_verified === 1 ? "Emprunter l'identité de cet utilisateur" : "Email non vérifié - Impossible d'emprunter l'identité"}
														disabled={user.email_verified !== 1}
													>
														<i className={user.email_verified === 1 ? "fas fa-sign-in-alt" : "fas fa-lock"}></i>
													</button>
													<button 
														className="btn btn-sm btn-primary" 
														onClick={() => handleEditUser(user)}
														style={{ borderRadius: '6px' }}
													>
														<i className="fas fa-edit me-1"></i>Modifier
													</button>
													<button 
														className="btn btn-sm btn-danger" 
														onClick={() => handleDelete(user)}
														style={{ borderRadius: '6px' }}
													>
														<i className="fas fa-trash me-1"></i>Delete
													</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
							{/* Pagination - Style like the image */}
							{totalUsers > 0 && (
								<div className="d-flex justify-content-between align-items-center mt-4 px-3 pb-3">
									<div className="text-muted">
										Affichage de {((currentPage - 1) * recordsPerPage) + 1} à {Math.min(currentPage * recordsPerPage, totalUsers)} sur {totalUsers} entrées
									</div>
									
									<div className="d-flex align-items-center">
										{/* Previous button */}
										<button
											type="button"
											onClick={handlePrevious}
											disabled={currentPage === 1}
											className="btn btn-sm me-1"
											style={{
												border: '1px solid #dee2e6',
												background: '#fff',
												color: currentPage === 1 ? '#6c757d' : '#007bff',
												cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
												width: '32px',
												height: '32px',
												borderRadius: '4px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center'
											}}
										>
											<i className="fas fa-angle-left"></i>
										</button>

										{/* Page numbers */}
										{getVisiblePages().map((pageNum) => (
											<button
												key={pageNum}
												type="button"
												onClick={() => handlePageClick(pageNum)}
												className="btn btn-sm me-1"
												style={{
													border: '1px solid #dee2e6',
													background: currentPage === pageNum ? '#17a2b8' : '#fff',
													color: currentPage === pageNum ? '#fff' : '#007bff',
													cursor: 'pointer',
													width: '32px',
													height: '32px',
													borderRadius: '4px',
													fontWeight: currentPage === pageNum ? 'bold' : 'normal'
												}}
											>
												{pageNum}
											</button>
										))}

										{/* Next button */}
										<button
											type="button"
											onClick={handleNext}
											disabled={currentPage >= totalPages}
											className="btn btn-sm"
											style={{
												border: '1px solid #dee2e6',
												background: '#fff',
												color: currentPage >= totalPages ? '#6c757d' : '#007bff',
												cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
												width: '32px',
												height: '32px',
												borderRadius: '4px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center'
											}}
										>
											<i className="fas fa-angle-right"></i>
										</button>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* Modal Ajouter Utilisateur */}
			<Modal 
				show={showModal} 
				onHide={handleCloseModal} 
				size="lg" 
				centered
				dialogClassName="custom-modal-width"
			>
				<Modal.Header closeButton style={{ borderBottom: '2px solid #1e7e34', padding: '24px 32px' }}>
					<Modal.Title style={{ color: '#1e7e34', fontWeight: '700', fontSize: '26px' }}>
						<i className="fas fa-user-plus me-3"></i>
						Ajouter un Utilisateur
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ padding: '32px 40px' }}>
					<form onSubmit={handleSubmit}>
						<div className="row g-4">
							{/* Nom */}
							<div className="col-md-6">
								<label className="form-label fw-bold" style={{ fontSize: '15px', marginBottom: '10px' }}>
									Nom complet <span className="text-danger">*</span>
								</label>
								<input
									type="text"
									className={`form-control form-control-lg ${formErrors.name ? 'is-invalid' : ''}`}
									name="name"
									value={formData.name}
									onChange={handleInputChange}
									placeholder="Ex: John Doe"
									style={{ 
										borderRadius: '10px', 
										fontSize: '16px',
										padding: '14px 18px',
										border: '2px solid #e0e0e0'
									}}
								/>
								{formErrors.name && (
									<div className="invalid-feedback" style={{ fontSize: '14px' }}>{formErrors.name}</div>
								)}
							</div>

							{/* Email */}
							<div className="col-md-6">
								<label className="form-label fw-bold" style={{ fontSize: '15px', marginBottom: '10px' }}>
									Email <span className="text-danger">*</span>
								</label>
								<input
									type="email"
									className={`form-control form-control-lg ${formErrors.email ? 'is-invalid' : ''}`}
									name="email"
									value={formData.email}
									onChange={handleInputChange}
									placeholder="Ex: john@example.com"
									style={{ 
										borderRadius: '10px', 
										fontSize: '16px',
										padding: '14px 18px',
										border: '2px solid #e0e0e0'
									}}
								/>
								{formErrors.email && (
									<div className="invalid-feedback" style={{ fontSize: '14px' }}>{formErrors.email}</div>
								)}
							</div>

							{/* Téléphone */}
							<div className="col-md-6">
								<label className="form-label fw-bold" style={{ fontSize: '15px', marginBottom: '10px' }}>
									Téléphone <span className="text-danger">*</span>
								</label>
								<input
									type="tel"
									className={`form-control form-control-lg ${formErrors.phone ? 'is-invalid' : ''}`}
									name="phone"
									value={formData.phone}
									onChange={handleInputChange}
									placeholder="Ex: +221701234567"
									style={{ 
										borderRadius: '10px', 
										fontSize: '16px',
										padding: '14px 18px',
										border: '2px solid #e0e0e0'
									}}
								/>
								{formErrors.phone && (
									<div className="invalid-feedback" style={{ fontSize: '14px' }}>{formErrors.phone}</div>
								)}
							</div>

							{/* Genre */}
							<div className="col-md-6">
								<label className="form-label fw-bold" style={{ fontSize: '15px', marginBottom: '10px' }}>
									Genre <span className="text-danger">*</span>
								</label>
								<select
									className="form-select form-select-lg"
									name="gender_id"
									value={formData.gender_id}
									onChange={handleInputChange}
									style={{ 
										borderRadius: '10px', 
										fontSize: '16px',
										padding: '14px 18px',
										border: '2px solid #e0e0e0'
									}}
								>
									<option value={1}>Homme</option>
									<option value={2}>Femme</option>
									<option value={3}>Autre</option>
								</select>
							</div>

							{/* Type d'utilisateur */}
							<div className="col-md-12">
								<label className="form-label fw-bold" style={{ fontSize: '15px', marginBottom: '10px' }}>
									Type d'utilisateur <span className="text-danger">*</span>
								</label>
								<select
									className="form-select form-select-lg"
									name="user_type_id"
									value={formData.user_type_id}
									onChange={handleInputChange}
									style={{ 
										borderRadius: '10px', 
										fontSize: '16px',
										padding: '14px 18px',
										border: '2px solid #e0e0e0'
									}}
								>
									<option value={1}>Utilisateur Standard</option>
									<option value={2}>Administrateur</option>
									<option value={3}>Modérateur</option>
								</select>
							</div>

							{/* Mot de passe */}
							<div className="col-md-6">
								<label className="form-label fw-bold" style={{ fontSize: '15px', marginBottom: '10px' }}>
									Mot de passe <span className="text-danger">*</span>
								</label>
								<input
									type="password"
									className={`form-control form-control-lg ${formErrors.password ? 'is-invalid' : ''}`}
									name="password"
									value={formData.password}
									onChange={handleInputChange}
									placeholder="Minimum 6 caractères"
									style={{ 
										borderRadius: '10px', 
										fontSize: '16px',
										padding: '14px 18px',
										border: '2px solid #e0e0e0'
									}}
								/>
								{formErrors.password && (
									<div className="invalid-feedback" style={{ fontSize: '14px' }}>{formErrors.password}</div>
								)}
							</div>

							{/* Confirmation mot de passe */}
							<div className="col-md-6">
								<label className="form-label fw-bold" style={{ fontSize: '15px', marginBottom: '10px' }}>
									Confirmer le mot de passe <span className="text-danger">*</span>
								</label>
								<input
									type="password"
									className={`form-control form-control-lg ${formErrors.password_confirmation ? 'is-invalid' : ''}`}
									name="password_confirmation"
									value={formData.password_confirmation}
									onChange={handleInputChange}
									placeholder="Répéter le mot de passe"
									style={{ 
										borderRadius: '10px', 
										fontSize: '16px',
										padding: '14px 18px',
										border: '2px solid #e0e0e0'
									}}
								/>
								{formErrors.password_confirmation && (
									<div className="invalid-feedback" style={{ fontSize: '14px' }}>{formErrors.password_confirmation}</div>
								)}
							</div>
						</div>
					</form>
				</Modal.Body>
				<Modal.Footer style={{ borderTop: '1px solid #dee2e6', padding: '20px 32px' }}>
					<button 
						type="button"
						className="btn btn-secondary btn-lg" 
						onClick={handleCloseModal}
						disabled={submitting}
						style={{ 
							borderRadius: '10px',
							fontSize: '16px',
							padding: '12px 28px',
							fontWeight: '600'
						}}
					>
						<i className="fas fa-times me-2"></i>
						Annuler
					</button>
					<button 
						type="button"
						className="btn btn-primary btn-lg" 
						onClick={handleSubmit}
						disabled={submitting}
						style={{ 
							borderRadius: '10px',
							background: 'linear-gradient(135deg, #1e7e34, #28a745)',
							border: 'none',
							fontSize: '16px',
							padding: '12px 28px',
							fontWeight: '600'
						}}
					>
						{submitting ? (
							<>
								<span className="spinner-border spinner-border-sm me-2"></span>
								Création...
							</>
						) : (
							<>
								<i className="fas fa-check me-2"></i>
								Créer l'utilisateur
							</>
						)}
					</button>
				</Modal.Footer>
			</Modal>

			{/* EDIT VIEW */}
			{view === 'edit' && currentUser && (
				<div className="edit-user-overlay" style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: '#f8f9fa',
					zIndex: 9999,
					overflowY: 'auto',
					padding: '40px 20px'
				}}>
					<div className="container" style={{ maxWidth: '1200px' }}>
						<div className="d-flex justify-content-between align-items-center mb-4">
							<h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
								Modifier
							</h2>
							<button 
								className="btn btn-secondary"
								onClick={() => setView('list')}
								style={{ borderRadius: '8px' }}
							>
								<i className="fas fa-times me-2"></i>Fermer
							</button>
						</div>

						<div className="row">
							{/* Colonne Gauche */}
							<div className="col-md-6">
								<div className="card mb-4" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
									<div className="card-body p-4">
										<div className="mb-3">
											<label className="form-label fw-bold">Nom</label>
											<input
												type="text"
												className="form-control"
												value={formData.name}
												onChange={(e) => setFormData({ ...formData, name: e.target.value })}
												style={{ borderRadius: '8px' }}
											/>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Prénom</label>
											<input
												type="text"
												className="form-control"
												placeholder="Prénom"
												style={{ borderRadius: '8px' }}
											/>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Nom d'utilisateur</label>
											<input
												type="text"
												className="form-control"
												value={formData.username}
												onChange={(e) => setFormData({ ...formData, username: e.target.value })}
												style={{ borderRadius: '8px' }}
											/>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Phone Number</label>
											<div className="input-group">
												<span className="input-group-text" style={{ borderRadius: '8px 0 0 8px' }}>
													🇸🇳 +221
												</span>
												<input
													type="text"
													className="form-control"
													value={formData.phone}
													onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
													style={{ borderRadius: '0 8px 8px 0' }}
												/>
											</div>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Email certifié</label>
											<div className="form-check form-switch">
												<input
													className="form-check-input"
													type="checkbox"
													checked={formData.email_verified}
													onChange={(e) => setFormData({ ...formData, email_verified: e.target.checked })}
													style={{ width: '48px', height: '24px', cursor: 'pointer' }}
												/>
											</div>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Téléphone certifié</label>
											<div className="form-check form-switch">
												<input
													className="form-check-input"
													type="checkbox"
													checked={formData.phone_verified}
													onChange={(e) => setFormData({ ...formData, phone_verified: e.target.checked })}
													style={{ width: '48px', height: '24px', cursor: 'pointer' }}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Colonne Droite */}
							<div className="col-md-6">
								<div className="card mb-4" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
									<div className="card-body p-4">
										<div className="mb-3">
											<label className="form-label fw-bold">Gender</label>
											<select 
												className="form-select"
												value={formData.gender_id}
												onChange={(e) => setFormData({ ...formData, gender_id: parseInt(e.target.value) })}
												style={{ borderRadius: '8px' }}
											>
												<option value="1">M</option>
												<option value="2">F</option>
											</select>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Email</label>
											<input
												type="email"
												className="form-control"
												value={formData.email}
												onChange={(e) => setFormData({ ...formData, email: e.target.value })}
												style={{ borderRadius: '8px' }}
											/>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Phone</label>
											<div className="input-group">
												<span className="input-group-text" style={{ borderRadius: '8px 0 0 8px' }}>
													🇸🇳 +221
												</span>
												<input
													type="text"
													className="form-control"
													value={formData.phone}
													onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
													style={{ borderRadius: '0 8px 8px 0' }}
												/>
											</div>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Country</label>
											<select className="form-select" style={{ borderRadius: '8px' }}>
												<option>Sénégal</option>
											</select>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Champ d'authentification principal</label>
											<div className="form-check">
												<input className="form-check-input" type="radio" name="auth_field" value="email" defaultChecked />
												<label className="form-check-label">Adresse email</label>
											</div>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Nouveau mot de passe</label>
											<input
												type="password"
												className="form-control"
												value={formData.password}
												onChange={(e) => setFormData({ ...formData, password: e.target.value })}
												style={{ borderRadius: '8px' }}
												placeholder="Laisser vide pour ne pas changer"
											/>
											<small className="text-muted">Laisser vide si vous ne voulez pas changer le mot de passe</small>
										</div>

										<div className="mb-3">
											<label className="form-label fw-bold">Confirmer le mot de passe</label>
											<input
												type="password"
												className="form-control"
												value={formData.password_confirmation}
												onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
												style={{ borderRadius: '8px' }}
												placeholder="Confirmer le nouveau mot de passe"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* User Role/Permissions */}
						<div className="card mb-4" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
							<div className="card-body p-4">
								<h5 className="mb-4">User Role/Permissions</h5>
								
								<div className="mb-4">
									<label className="form-label fw-bold">Roles</label>
									<div className="row g-3">
										{roles.map((role) => (
											<div key={role.id} className="col-md-4">
												<div className="form-check">
													<input
														className="form-check-input"
														type="checkbox"
														id={`role-${role.id}`}
														checked={formData.roles.includes(role.name)}
														onChange={(e) => {
															if (e.target.checked) {
																setFormData({ ...formData, roles: [...formData.roles, role.name] });
															} else {
																setFormData({ ...formData, roles: formData.roles.filter(r => r !== role.name) });
															}
														}}
														style={{ accentColor: '#3b82f6' }}
													/>
													<label className="form-check-label" htmlFor={`role-${role.id}`}>
														{role.name}
													</label>
												</div>
											</div>
										))}
									</div>
								</div>

								<div className="mb-4">
									<label className="form-label fw-bold">Permissions</label>
									<div className="row g-3">
										{permissions.map((permission) => (
											<div key={permission.id} className="col-md-4">
												<div className="form-check">
													<input
														className="form-check-input"
														type="checkbox"
														id={`perm-${permission.id}`}
														checked={formData.permissions.includes(permission.name)}
														onChange={(e) => {
															if (e.target.checked) {
																setFormData({ ...formData, permissions: [...formData.permissions, permission.name] });
															} else {
																setFormData({ ...formData, permissions: formData.permissions.filter(p => p !== permission.name) });
															}
														}}
														style={{ accentColor: '#3b82f6' }}
													/>
													<label className="form-check-label" htmlFor={`perm-${permission.id}`}>
														{permission.name}
													</label>
												</div>
											</div>
										))}
									</div>
								</div>

								<div className="d-flex gap-2">
									<button 
										className="btn btn-primary"
										onClick={handleUpdateUser}
										disabled={submitting}
										style={{ 
											borderRadius: '8px',
											background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
											border: 'none',
											padding: '12px 28px',
											fontWeight: '500'
										}}
									>
										{submitting ? (
											<>
												<span className="spinner-border spinner-border-sm me-2"></span>
												Enregistrement...
											</>
										) : (
											<>
												<i className="fas fa-save me-2"></i>
												Enregistrer et Retour à la list
											</>
										)}
									</button>
									<button 
										className="btn btn-secondary"
										onClick={() => setView('list')}
										style={{ borderRadius: '8px', padding: '12px 28px', fontWeight: '500' }}
									>
										<i className="fas fa-times me-2"></i>Annuler
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Modal de confirmation de suppression */}
			<Modal 
				show={showDeleteModal} 
				onHide={cancelDelete}
				centered
				size="sm"
			>
				<Modal.Body className="text-center py-4">
					<div className="mb-3">
						<div 
							className="mx-auto mb-3"
							style={{
								width: '60px',
								height: '60px',
								borderRadius: '50%',
								border: '3px solid #ffc107',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: '#fff3cd'
							}}
						>
							<i className="fas fa-exclamation" style={{ fontSize: '24px', color: '#ffc107' }}></i>
						</div>
					</div>
					
					<h5 className="mb-3" style={{ fontWeight: '600', color: '#333' }}>
						Êtes-vous sûr?
					</h5>
					
					<p className="text-muted mb-4" style={{ fontSize: '14px' }}>
						Voulez-vous vraiment supprimer l'utilisateur <strong>{userToDelete?.name}</strong>?
					</p>
					
					<div className="d-flex justify-content-center gap-3">
						<button
							type="button"
							className="btn btn-secondary px-4"
							onClick={cancelDelete}
							disabled={deleting}
							style={{ 
								borderRadius: '6px',
								fontWeight: '500'
							}}
						>
							Cancel
						</button>
						<button
							type="button"
							className="btn btn-danger px-4"
							onClick={confirmDelete}
							disabled={deleting}
							style={{ 
								borderRadius: '6px',
								fontWeight: '500'
							}}
						>
							{deleting ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status"></span>
									Suppression...
								</>
							) : (
								'OK'
							)}
						</button>
					</div>
				</Modal.Body>
			</Modal>

			{/* Modal de confirmation d'emprunt d'identité */}
			<Modal 
				show={showImpersonateModal} 
				onHide={cancelImpersonate}
				centered
				size="sm"
			>
				<Modal.Body className="text-center py-4">
					<div className="mb-3">
						<div 
							className="mx-auto mb-3"
							style={{
								width: '60px',
								height: '60px',
								borderRadius: '50%',
								border: '3px solidrgb(36, 184, 23)',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: '#d1ecf1'
							}}
						>
							<i className="fas fa-user-secret" style={{ fontSize: '24px', color: '#17a2b8' }}></i>
						</div>
					</div>
					
					<h5 className="mb-3" style={{ fontWeight: '600', color: '#333' }}>
						Emprunter l'identité ?
					</h5>
					
					<p className="text-muted mb-4" style={{ fontSize: '14px' }}>
						Voulez-vous emprunter l'identité de <strong>{userToImpersonate?.name}</strong> ?<br/>
						<small className="text-info">Vous serez redirigé vers son dashboard et pourrez agir en son nom.</small>
					</p>
					
					<div className="d-flex justify-content-center gap-3">
						<button
							type="button"
							className="btn btn-secondary px-4"
							onClick={cancelImpersonate}
							disabled={impersonating}
							style={{ 
								borderRadius: '6px',
								fontWeight: '500'
							}}
						>
							Annuler
						</button>
						<button
							type="button"
							className="btn btn-info px-4"
							onClick={confirmImpersonate}
							disabled={impersonating}
							style={{ 
								borderRadius: '6px',
								fontWeight: '500'
							}}
						>
							{impersonating ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status"></span>
									Connexion...
								</>
							) : (
								<>
									<i className="fas fa-sign-in-alt me-2"></i>
									Emprunter
								</>
							)}
						</button>
					</div>
				</Modal.Body>
			</Modal>
		</>
	);
};

export default UsersList;
